import React, { useEffect, useState } from "react";
import {
  Input,
  Card,
  Row,
  Col,
  Empty,
  Typography,
  Tag,
  Divider,
  Space,
  Button,
  Select,
  Drawer,
  Spin,
} from "antd";
import {
  DollarOutlined,
  CalendarOutlined,
  MessageOutlined,
  EnvironmentOutlined,
  UserOutlined,
  LaptopOutlined,
  ClockCircleOutlined,
  FilterOutlined,
  SendOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";
import { API_CONST } from "../../../const";
import styled from "styled-components";
import { OngoingJobsWrapper } from "./OngoingJobs.style";

const PageHeader = styled.div`
  margin-bottom: 24px;
  h1 { font-size: 24px; font-weight: 600; color: #014c75; margin: 0 0 8px 0; }
  p { color: #666; margin: 0; font-size: 14px; }
`;

const { Search } = Input;
const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

const dummyJobs = [
  {
    id: "JOB-2024-001",
    title: "Senior React Developer",
    company: "TechCorp Solutions",
    companyType: "Product",
    location: "Remote",
    industry: "IT Services",
    openPositions: 3,
    designation: "Senior React Developer",
    experience: "5-7 years",
    employmentType: "Full Time",
    contractType: "Contract",
    workingMode: "Remote",
    salary: "₹1,80,000 / Month",
    salaryPerMonth: "1,80,000",
    skills: ["React", "TypeScript", "Node.js"],
    interested: 24,
    primarySkills: ["React.js", "TypeScript", "Redux", "Next.js"],
    goodToHaveSkills: ["GraphQL", "AWS", "Docker"],
    projectDuration: "6 Months",
    communication: "Excellent",
    startDate: "15 Dec 2024",
    systemProvided: true,
    workTime: "9 AM - 6 PM IST",
    timeZone: "IST",
    jobDescription: "We are seeking an experienced Senior React Developer to join our dynamic engineering team. The ideal candidate will lead the development of modern, scalable web applications using React.js and related technologies.\n\nRequired Experience:\n- 5-7 years of professional experience in front-end development\n- Strong expertise in React.js, including hooks, context API, and component lifecycle\n- Advanced proficiency in TypeScript for type-safe application development\n- Experience with state management libraries like Redux or MobX\n- Solid understanding of modern JavaScript (ES6+) and web standards\n- Hands-on experience with Next.js for server-side rendering\n- Proficiency in responsive design and cross-browser compatibility\n\nKey Responsibilities:\n- Design and develop scalable, high-performance web applications using React.js\n- Collaborate with UX/UI designers to implement pixel-perfect user interfaces\n- Write clean, maintainable, and well-documented code following industry best practices\n- Lead code reviews and provide constructive feedback to team members\n- Mentor junior developers and contribute to team knowledge sharing\n- Optimize application performance and ensure excellent user experience\n- Participate in architectural decisions and technical planning sessions\n- Stay updated with emerging technologies and industry trends\n\nQualifications:\n- Bachelor's degree in Computer Science or equivalent practical experience\n- Strong problem-solving skills and attention to detail\n- Excellent communication and teamwork abilities\n- Experience with agile development methodologies\n- Familiarity with Git version control and CI/CD pipelines"
  },
  {
    id: "JOB-2024-002",
    title: "Full Stack Java Developer",
    company: "Enterprise Systems Inc",
    companyType: "Service",
    location: "Bangalore",
    industry: "Enterprise Software",
    openPositions: 2,
    designation: "Full Stack Java Developer",
    experience: "4-6 years",
    employmentType: "Contract",
    contractType: "Contract",
    workingMode: "Hybrid",
    salary: "₹1,50,000 / Month",
    salaryPerMonth: "1,50,000",
    skills: ["Java", "Spring Boot", "Angular"],
    interested: 18,
    primarySkills: ["Java", "Spring Boot", "Microservices", "Angular"],
    goodToHaveSkills: ["Kubernetes", "Jenkins", "MySQL"],
    projectDuration: "12 Months",
    communication: "Good",
    startDate: "1 Jan 2025",
    systemProvided: false,
    workTime: "10 AM - 7 PM IST",
    timeZone: "IST",
    jobDescription: "Join our enterprise software team as a Full Stack Java Developer to build robust, scalable applications for Fortune 500 clients. You'll work on cutting-edge microservices architecture using Java Spring Boot and Angular.\n\nRequired Experience:\n- 4-6 years of full-stack development experience with Java technologies\n- Strong proficiency in Java 8+ and Spring Boot framework\n- Experience designing and implementing RESTful APIs\n- Solid understanding of microservices architecture and design patterns\n- Front-end development experience with Angular 12+\n- Working knowledge of SQL and NoSQL databases\n- Experience with containerization using Docker\n\nKey Responsibilities:\n- Design and develop enterprise-grade applications using Java Spring Boot\n- Build and maintain microservices for distributed systems\n- Create responsive front-end interfaces using Angular framework\n- Implement secure RESTful APIs and integrate third-party services\n- Write comprehensive unit tests and integration tests\n- Optimize database queries and application performance\n- Collaborate with DevOps team for deployment and monitoring\n- Participate in requirement analysis and technical documentation\n\nQualifications:\n- Bachelor's degree in Computer Science, Engineering, or related field\n- Strong analytical and problem-solving capabilities\n- Experience with agile/scrum methodologies\n- Knowledge of design patterns and software architecture principles\n- Excellent verbal and written communication skills"
  },
  {
    id: "JOB-2024-003",
    title: "DevOps Engineer",
    company: "CloudNative Labs",
    companyType: "Startup",
    location: "Hyderabad",
    industry: "Cloud Services",
    openPositions: 1,
    designation: "DevOps Engineer",
    experience: "3-5 years",
    employmentType: "Full Time",
    contractType: "Permanent",
    workingMode: "Office",
    salary: "₹1,25,000 / Month",
    salaryPerMonth: "1,25,000",
    skills: ["AWS", "Docker", "Kubernetes"],
    interested: 15,
    primarySkills: ["AWS", "Docker", "Kubernetes", "Jenkins", "Terraform"],
    goodToHaveSkills: ["Ansible", "Prometheus", "Grafana"],
    projectDuration: "Permanent Role",
    communication: "Excellent",
    startDate: "20 Dec 2024",
    systemProvided: true,
    workTime: "Flexible",
    timeZone: "IST",
    jobDescription: "We're looking for a skilled DevOps Engineer to join our cloud infrastructure team. You'll be responsible for building and maintaining our AWS-based infrastructure, implementing CI/CD pipelines, and ensuring system reliability.\n\nRequired Experience:\n- 3-5 years of hands-on DevOps experience in production environments\n- Strong expertise in AWS cloud services (EC2, S3, RDS, Lambda, etc.)\n- Proficiency in containerization using Docker and orchestration with Kubernetes\n- Experience with Infrastructure as Code using Terraform or CloudFormation\n- Solid understanding of CI/CD pipelines using Jenkins or GitLab CI\n- Working knowledge of Linux system administration\n- Experience with monitoring and logging tools\n\nKey Responsibilities:\n- Design, implement, and manage scalable AWS cloud infrastructure\n- Build and maintain automated CI/CD pipelines for multiple applications\n- Manage Kubernetes clusters and containerized applications\n- Implement infrastructure as code using Terraform\n- Set up monitoring, alerting, and logging systems (Prometheus, Grafana, ELK)\n- Ensure high availability and disaster recovery procedures\n- Implement security best practices and compliance requirements\n- Collaborate with development teams to optimize deployment processes\n- Troubleshoot production issues and perform root cause analysis\n\nQualifications:\n- Bachelor's degree in Computer Science or related technical field\n- Strong scripting skills (Python, Bash, or similar)\n- Understanding of networking concepts and security principles\n- Experience with version control systems (Git)\n- Excellent problem-solving and communication skills"
  },
  {
    id: "JOB-2024-004",
    title: "Python Backend Developer",
    company: "DataDriven AI",
    companyType: "Product",
    location: "Pune",
    industry: "Artificial Intelligence",
    openPositions: 4,
    designation: "Python Backend Developer",
    experience: "2-4 years",
    employmentType: "Full Time",
    contractType: "Contract",
    workingMode: "Remote",
    salary: "₹1,00,000 / Month",
    salaryPerMonth: "1,00,000",
    skills: ["Python", "Django", "PostgreSQL"],
    interested: 32,
    primarySkills: ["Python", "Django", "FastAPI", "PostgreSQL", "Redis"],
    goodToHaveSkills: ["Celery", "RabbitMQ", "MongoDB"],
    projectDuration: "9 Months",
    communication: "Good",
    startDate: "10 Jan 2025",
    systemProvided: true,
    workTime: "9 AM - 6 PM IST",
    timeZone: "IST",
    jobDescription: "Join our AI-driven product team as a Python Backend Developer. You'll build scalable backend services that power our machine learning applications and data processing pipelines.\n\nRequired Experience:\n- 2-4 years of backend development experience with Python\n- Strong proficiency in Django or Flask framework\n- Experience with FastAPI for building high-performance APIs\n- Solid understanding of RESTful API design principles\n- Working experience with PostgreSQL and database optimization\n- Knowledge of Redis for caching and session management\n- Familiarity with asynchronous task processing\n\nKey Responsibilities:\n- Develop and maintain robust backend services using Python and Django\n- Design and implement RESTful APIs for web and mobile applications\n- Work with data science team to integrate ML models into production\n- Optimize database queries and implement efficient data models\n- Implement caching strategies using Redis for improved performance\n- Build asynchronous task processing using Celery\n- Write comprehensive unit tests and API documentation\n- Ensure code quality through code reviews and best practices\n- Monitor application performance and troubleshoot production issues\n\nQualifications:\n- Bachelor's degree in Computer Science or related field\n- Strong understanding of data structures and algorithms\n- Experience with Git version control\n- Knowledge of software testing methodologies\n- Good communication and teamwork skills"
  },
  {
    id: "JOB-2024-005",
    title: "Mobile App Developer (React Native)",
    company: "MobileFirst Tech",
    companyType: "Startup",
    location: "Remote",
    industry: "Mobile Technology",
    openPositions: 2,
    designation: "Mobile App Developer",
    experience: "3-5 years",
    employmentType: "Contract",
    contractType: "Contract",
    workingMode: "Remote",
    salary: "₹1,40,000 / Month",
    salaryPerMonth: "1,40,000",
    skills: ["React Native", "JavaScript", "iOS"],
    interested: 21,
    primarySkills: ["React Native", "JavaScript", "TypeScript", "Redux"],
    goodToHaveSkills: ["Swift", "Kotlin", "Firebase"],
    projectDuration: "8 Months",
    communication: "Excellent",
    startDate: "5 Jan 2025",
    systemProvided: false,
    workTime: "10 AM - 7 PM IST",
    timeZone: "IST",
    jobDescription: "We're seeking an experienced React Native Developer to build innovative mobile applications for both iOS and Android platforms. You'll work on consumer-facing apps with millions of users.\n\nRequired Experience:\n- 3-5 years of mobile app development experience with React Native\n- Strong proficiency in JavaScript and TypeScript\n- Experience building and deploying apps to App Store and Play Store\n- Solid understanding of React Native architecture and native modules\n- Working knowledge of Redux or other state management solutions\n- Experience with RESTful APIs and third-party integrations\n- Familiarity with mobile app performance optimization\n\nKey Responsibilities:\n- Develop cross-platform mobile applications using React Native\n- Build reusable components and maintain code quality standards\n- Integrate with native modules when required (iOS/Android)\n- Implement push notifications, deep linking, and analytics\n- Optimize app performance and minimize bundle size\n- Write unit tests and ensure code coverage\n- Collaborate with designers to implement pixel-perfect UIs\n- Debug and fix issues across different devices and OS versions\n- Maintain app releases and handle App Store submissions\n\nQualifications:\n- Bachelor's degree in Computer Science or equivalent experience\n- Strong problem-solving and debugging skills\n- Experience with Git and agile development processes\n- Understanding of mobile app security best practices\n- Excellent attention to detail and user experience"
  },
];

const computeMatchScores = (job, benchTalents) => {
  const normalize = (s) => String(s || "").toLowerCase().trim();
  const jobPrimary = (job.primarySkills || []).map(normalize).filter(Boolean);
  const jobSecondary = (job.goodToHaveSkills || []).map(normalize).filter(Boolean);
  const hasPrimary = jobPrimary.length > 0;
  const hasSecondary = jobSecondary.length > 0;
  if (!hasPrimary && !hasSecondary) return { count: 0, bestPct: 0 };

  const parseExpRange = (range) => {
    if (!range) return null;
    const str = String(range).toLowerCase().replace(/years?/g, '').trim();
    const rm = str.match(/(\d+(?:\.\d+)?)\s*[-–]+\s*(\d+(?:\.\d+)?)/);
    if (rm) return { min: parseFloat(rm[1]), max: parseFloat(rm[2]) };
    const pm = str.match(/(\d+(?:\.\d+)?)\s*\+/);
    if (pm) { const v = parseFloat(pm[1]); return { min: v, max: v + 5 }; }
    const sm = str.match(/(\d+(?:\.\d+)?)/);
    if (sm) { const v = parseFloat(sm[1]); return { min: Math.max(0, v - 1), max: v + 1 }; }
    return null;
  };
  const expRange = parseExpRange(job.experience);

  const skillMatch = (ts, js) => ts.includes(js) || js.includes(ts);
  let count = 0;
  let bestPct = 0;

  benchTalents.forEach((talent) => {
    const allTalentSkills = [
      ...(talent.primarySkills || []),
      ...(talent.secondarySkills || []),
    ].map(normalize).filter(Boolean);

    const primaryHits = jobPrimary.filter((js) =>
      allTalentSkills.some((ts) => skillMatch(ts, js))
    ).length;
    const secondaryHits = jobSecondary.filter((js) =>
      allTalentSkills.some((ts) => skillMatch(ts, js))
    ).length;

    let expScore = 0.5;
    if (expRange !== null) {
      const yrs = !isNaN(parseFloat(talent.experienceYears))
        ? parseFloat(talent.experienceYears) + (parseFloat(talent.experienceMonths) || 0) / 12
        : null;
      if (yrs === null) {
        expScore = 0.5;
      } else if (yrs >= expRange.min && yrs <= expRange.max) {
        expScore = 1.0;
      } else if (yrs > expRange.max) {
        expScore = 0.8;
      } else {
        const gap = expRange.min - yrs;
        expScore = gap <= 1 ? 0.5 : gap <= 2 ? 0.25 : 0;
      }
    }

    let pct;
    if (hasPrimary && hasSecondary) {
      pct = Math.round((primaryHits / jobPrimary.length) * 60 + (secondaryHits / jobSecondary.length) * 20 + expScore * 20);
    } else if (hasPrimary) {
      pct = Math.round((primaryHits / jobPrimary.length) * 80 + expScore * 20);
    } else {
      pct = Math.round((secondaryHits / jobSecondary.length) * 80 + expScore * 20);
    }
    pct = Math.min(100, Math.max(0, pct));

    if (pct > 0) count++;
    if (pct > bestPct) bestPct = pct;
  });

  return { count, bestPct };
};

const OngoingJobs = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const initialSearch = searchParams.get("search") || "";
  const [search, setSearch] = useState(initialSearch);
  const [selectedJob, setSelectedJob] = useState(null);
  const [jobsData, setUsersData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [moreFiltersVisible, setMoreFiltersVisible] = useState(false);
  const [matchCounts, setMatchCounts] = useState({});
  const [myClientsOnly, setMyClientsOnly] = useState(
    () => sessionStorage.getItem("ongoingJobs_myClientsOnly") === "true"
  );
  
  const [filters, setFilters] = useState({
    location: null,
    primarySkill: null,
    companyType: null,
    industry: null,
    experience: null,
    contractType: null,
    workingMode: null,
  });

  useEffect(() => {
    const newSearchParams = new URLSearchParams(location.search);
    const urlSearch = newSearchParams.get("search") || "";
    if (urlSearch && urlSearch !== search) {
      setSearch(urlSearch);
    }
  }, [location.search]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("authToken");

        let customerOrganizations;
        if (myClientsOnly) {
          const relResponse = await fetch(API_CONST.PARTNER_RELATION_MY_CLIENTS, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const relData = await relResponse.json();
          const activeClients = (relData.relations || []).filter(
            (r) => r.status === "active" && r.customerOrganization
          );
          if (activeClients.length === 0) {
            setUsersData([]);
            setLoading(false);
            return;
          }
          customerOrganizations = activeClients.map((r) => r.customerOrganization);
        }

        const body = { status: "open" };
        if (customerOrganizations) body.customerOrganizations = customerOrganizations;

        const response = await fetch(API_CONST.GET_JOB_REQUIREMENTS, {
          method: "POST",
          headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        });

        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        const result = await response.json();
        if (result.Response && result.Response.length > 0) {
          const mappedJobs = result.Response.map(job => ({
            id: job.id,
            title: job.role,
            designation: job.role,
            location: job.reportingLocation || "Remote",
            companyType: job.engagementType || "Contract",
            industry: "Technology",
            openPositions: job.numberOfRequirements || 1,
            experience: typeof job.experienceRange === 'string' && job.experienceRange
              ? job.experienceRange
              : (job.experienceRange?.min != null && job.experienceRange?.max != null)
                ? `${job.experienceRange.min}-${job.experienceRange.max} years`
                : "Not specified",
            employmentType: job.engagementType === "Full Time" ? "Full Time" : "Contract",
            contractType: job.engagementType || "Contract",
            workingMode: job.worldwide ? "Remote" : "Office",
            salaryAmount: `₹${Number(job.budget || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`,
            salaryPeriod: job.budgetPeriod === 'Total' ? 'Total' : 'Monthly',
            salary: `₹${Number(job.budget || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })} ${job.budgetPeriod === 'Total' ? 'Total' : 'Monthly'}`,
            salaryPerMonth: job.budget?.toString() || "0",
            skills: job.primarySkills?.slice(0, 3) || [],
            interested: job.interested || 0,
            primarySkills: job.primarySkills || [],
            goodToHaveSkills: job.secondarySkills || [],
            projectDuration: job.monthsOfEngagement
              ? `${String(job.monthsOfEngagement).replace(/\s*months?\s*/gi, '').trim()} Months`
              : "Not specified",
            communication: job.communication || "Good",
            startDate: job.tentativeStartDate ? new Date(job.tentativeStartDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : "Not specified",
            systemProvided: job.provideSystem || false,
            workTime: job.shiftTiming === "Flexible" ? "Flexible" : `${job.shiftStart || "9 AM"} - ${job.shiftEnd || "6 PM"} IST`,
            timeZone: job.workingTime || "IST",
            jobDescription: job.jobResponsibilities || "No description available",
          }));
          setUsersData(mappedJobs);
          setSelectedJob(mappedJobs[0]);

          try {
            const benchResponse = await fetch(API_CONST.GET_TALENT_PROFILES, {
              method: "POST",
              headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" },
              body: JSON.stringify({ status: "Active", pageSize: 10000 }),
            });
            if (benchResponse.ok) {
              const benchData = await benchResponse.json();
              const benchTalents = benchData.talents || benchData.Response || [];
              const counts = {};
              mappedJobs.forEach((job) => {
                counts[job.id] = computeMatchScores(job, benchTalents);
              });
              setMatchCounts(counts);
            }
          } catch (benchErr) {
            console.log("Error fetching bench pool for matching:", benchErr.message);
          }
        } else {
          setUsersData([]);
        }
      } catch (error) {
        console.log("Error fetching jobs:", error.message);
        setUsersData([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [myClientsOnly]);

  const handleMyClientsToggle = () => {
    const next = !myClientsOnly;
    setMyClientsOnly(next);
    sessionStorage.setItem("ongoingJobs_myClientsOnly", String(next));
  };

  const filteredJobs = jobsData.filter((job) => {
    const matchesSearch =
      job.title?.toLowerCase().includes(search.toLowerCase()) ||
      job.company?.toLowerCase().includes(search.toLowerCase()) ||
      job.designation?.toLowerCase().includes(search.toLowerCase());

    const matchesLocation = !filters.location || job.location === filters.location;
    const matchesCompanyType = !filters.companyType || job.companyType === filters.companyType;
    const matchesIndustry = !filters.industry || job.industry === filters.industry;
    const matchesExperience = !filters.experience || job.experience === filters.experience;
    const matchesContractType = !filters.contractType || job.contractType === filters.contractType;
    const matchesWorkingMode = !filters.workingMode || job.workingMode === filters.workingMode;
    const matchesPrimarySkill = !filters.primarySkill || 
      job.primarySkills?.some(skill => skill.toLowerCase().includes(filters.primarySkill.toLowerCase()));

    return matchesSearch && matchesLocation && matchesCompanyType && matchesIndustry && 
           matchesExperience && matchesContractType && matchesWorkingMode && matchesPrimarySkill;
  });

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({ ...prev, [filterName]: value }));
  };

  const clearAllFilters = () => {
    setFilters({
      location: null,
      primarySkill: null,
      companyType: null,
      industry: null,
      experience: null,
      contractType: null,
      workingMode: null,
    });
  };

  const locations = [...new Set(jobsData.map(job => job.location))];
  const companyTypes = [...new Set(jobsData.map(job => job.companyType))];
  const industries = [...new Set(jobsData.map(job => job.industry))];
  const experiences = [...new Set(jobsData.map(job => job.experience))];
  const contractTypes = [...new Set(jobsData.map(job => job.contractType))];
  const workingModes = [...new Set(jobsData.map(job => job.workingMode))];
  const allSkills = [...new Set(jobsData.flatMap(job => job.primarySkills || []))];

  const activeFilterCount = Object.values(filters).filter(v => v !== null).length;
  const moreFiltersCount = [filters.companyType, filters.industry, filters.experience, filters.contractType, filters.workingMode].filter(v => v !== null).length;

  return (
    <OngoingJobsWrapper>
      <PageHeader>
        <h1>Ongoing Jobs</h1>
        <p>View and manage active job opportunities</p>
      </PageHeader>
      <div className="search-section">
        <Row gutter={[24, 12]} align="middle">
          <Col xs={24} md={10}>
            <Input
              placeholder="Search jobs by title, company, or designation..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              prefix={<SearchOutlined />}
              allowClear
              size="large"
              className="search-input"
            />
          </Col>
          <Col xs={24} md={14}>
            <div className="search-filters-row">
              <Select
                placeholder="Location"
                allowClear
                value={filters.location}
                onChange={(value) => handleFilterChange('location', value)}
                className="primary-filter"
                size="large"
              >
                {locations.map(loc => (
                  <Option key={loc} value={loc}>{loc}</Option>
                ))}
              </Select>

              <Select
                placeholder="Primary Skill"
                allowClear
                showSearch
                value={filters.primarySkill}
                onChange={(value) => handleFilterChange('primarySkill', value)}
                className="primary-filter"
                size="large"
              >
                {allSkills.map(skill => (
                  <Option key={skill} value={skill}>{skill}</Option>
                ))}
              </Select>

              <Button
                icon={<FilterOutlined />}
                onClick={() => setMoreFiltersVisible(true)}
                size="large"
                className="more-filters-btn"
              >
                More Filters {moreFiltersCount > 0 && `(${moreFiltersCount})`}
              </Button>

              <Button
                size="large"
                type={myClientsOnly ? "primary" : "default"}
                onClick={handleMyClientsToggle}
                style={myClientsOnly ? { background: "#014c75", borderColor: "#014c75" } : {}}
              >
                My Clients Only
              </Button>

              {activeFilterCount > 0 && (
                <Button onClick={clearAllFilters} type="link" size="large" className="clear-all-btn">
                  Clear All
                </Button>
              )}
            </div>
          </Col>
        </Row>
      </div>

      <Drawer
        title="More Filters"
        placement="right"
        onClose={() => setMoreFiltersVisible(false)}
        open={moreFiltersVisible}
        width={400}
      >
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div>
            <Text strong>Company Type</Text>
            <Select
              placeholder="Select company type"
              allowClear
              value={filters.companyType}
              onChange={(value) => handleFilterChange('companyType', value)}
              style={{ width: '100%', marginTop: 8 }}
            >
              {companyTypes.map(type => (
                <Option key={type} value={type}>{type}</Option>
              ))}
            </Select>
          </div>

          <div>
            <Text strong>Industry</Text>
            <Select
              placeholder="Select industry"
              allowClear
              value={filters.industry}
              onChange={(value) => handleFilterChange('industry', value)}
              style={{ width: '100%', marginTop: 8 }}
            >
              {industries.map(ind => (
                <Option key={ind} value={ind}>{ind}</Option>
              ))}
            </Select>
          </div>

          <div>
            <Text strong>Experience</Text>
            <Select
              placeholder="Select experience"
              allowClear
              value={filters.experience}
              onChange={(value) => handleFilterChange('experience', value)}
              style={{ width: '100%', marginTop: 8 }}
            >
              {experiences.map(exp => (
                <Option key={exp} value={exp}>{exp}</Option>
              ))}
            </Select>
          </div>

          <div>
            <Text strong>Contract Type</Text>
            <Select
              placeholder="Select contract type"
              allowClear
              value={filters.contractType}
              onChange={(value) => handleFilterChange('contractType', value)}
              style={{ width: '100%', marginTop: 8 }}
            >
              {contractTypes.map(type => (
                <Option key={type} value={type}>{type}</Option>
              ))}
            </Select>
          </div>

          <div>
            <Text strong>Working Mode</Text>
            <Select
              placeholder="Select working mode"
              allowClear
              value={filters.workingMode}
              onChange={(value) => handleFilterChange('workingMode', value)}
              style={{ width: '100%', marginTop: 8 }}
            >
              {workingModes.map(mode => (
                <Option key={mode} value={mode}>{mode}</Option>
              ))}
            </Select>
          </div>
        </Space>
      </Drawer>

      <Row gutter={[24, 24]} className="content-section">
        <Col xs={24} md={10} className="jobs-list-column">
          <div className="jobs-scroll-container">
            {loading ? (
              <div style={{ textAlign: 'center', padding: '50px 0' }}>
                <Spin size="large" />
              </div>
            ) : filteredJobs.length === 0 ? (
              <Empty description="No jobs found." />
            ) : (
              filteredJobs.map((job) => (
                <Card
                  key={job.id}
                  hoverable
                  onClick={() => setSelectedJob(job)}
                  className={`job-list-card ${selectedJob?.id === job.id ? 'selected' : ''}`}
                >
                  <div className="card-meta-row">
                    <span className="card-meta-item">
                      <LaptopOutlined /> {job.contractType}
                    </span>
                    <span className="card-meta-item">
                      <EnvironmentOutlined /> {job.location}
                    </span>
                    <span className="card-meta-item">
                      <UserOutlined /> {job.openPositions} Open
                    </span>
                  </div>

                  <div className="card-title">
                    {job.designation}
                    {job.experience && job.experience !== "Not specified" && (
                      <span className="card-exp"> ({job.experience} Exp)</span>
                    )}
                  </div>

                  <div className="card-salary-row">
                    <Tag
                      color={job.employmentType === "Full Time" ? "green" : "orange"}
                      className="card-type-tag"
                    >
                      {job.employmentType}
                    </Tag>
                    <span className="card-salary-amount">
                      {job.salaryAmount}
                      <span className="card-salary-period"> / mo</span>
                    </span>
                  </div>

                  {job.skills?.length > 0 && (
                    <div className="card-skills-row">
                      <Space wrap size={[4, 4]}>
                        {job.skills.map((skill) => (
                          <Tag key={skill} color="geekblue">{skill}</Tag>
                        ))}
                      </Space>
                    </div>
                  )}

                  {matchCounts[job.id] !== undefined && (
                    <div className="card-match-row">
                      <span className={`card-match-pill ${matchCounts[job.id].count > 0 ? 'has-match' : ''}`}>
                        <UserOutlined /> Matching profiles: {matchCounts[job.id].count}
                      </span>
                      {matchCounts[job.id].count > 0 && (() => {
                        const pct = matchCounts[job.id].bestPct;
                        const tier = pct >= 70 ? 'high' : pct >= 40 ? 'mid' : 'low';
                        return (
                          <span className={`card-match-pct-pill pct-${tier}`}>
                            ⚡ Best match: {pct}%
                          </span>
                        );
                      })()}
                    </div>
                  )}
                </Card>
              ))
            )}
          </div>
        </Col>

        <Col xs={24} md={14} className="job-details-column">
          <Card className="details-card">
            {selectedJob ? (
              <>
                <Row gutter={[16, 16]} className="job-header-row" align="middle">
                  <Col xs={24} sm={16}>
                    <Row gutter={[12, 8]} align="middle">
                      <Col xs={24} sm={24}>
                        <Space size={16} wrap>
                          <div>
                            <Text strong className="label">Job ID: </Text>
                            <Tag color="blue">{selectedJob.id}</Tag>
                          </div>
                          <Text className="info-text">
                            <UserOutlined style={{ marginRight: 4 }} />
                            {selectedJob.interested} Interested
                          </Text>
                          <Text className="info-text">
                            <EnvironmentOutlined style={{ marginRight: 4 }} />
                            {selectedJob.location}
                          </Text>
                          <Text className="info-text">
                            <LaptopOutlined style={{ marginRight: 4 }} />
                            {selectedJob.openPositions} Open
                          </Text>
                        </Space>
                      </Col>
                    </Row>
                  </Col>
                  <Col xs={24} sm={8} className="submit-btn-col">
                    <Button
                      type="primary"
                      size="large"
                      icon={<SendOutlined />}
                      className="submit-profiles-btn"
                      onClick={() => navigate(`/partner/submit-profiles/${selectedJob.id}`)}
                      block
                    >
                      Submit Profiles
                    </Button>
                  </Col>
                </Row>
                
                <Divider />

                <div className="title-row">
                  <Title level={4}>
                    {selectedJob.designation}
                    {selectedJob.experience && selectedJob.experience !== "Not specified" && (
                      <span className="exp-badge"> ({selectedJob.experience} Exp)</span>
                    )}
                  </Title>
                </div>

                <div className="salary-hero-row">
                  <span className="salary-hero-amount">
                    <DollarOutlined /> {selectedJob.salaryAmount}
                  </span>
                  <span className="salary-hero-period">{selectedJob.salaryPeriod}</span>
                </div>

                <div className="meta-pills-row">
                  <span className="meta-pill">
                    <CalendarOutlined className="meta-icon" />
                    <span className="meta-label">Duration</span>
                    <span className="meta-value">{selectedJob.projectDuration}</span>
                  </span>
                  <span className="meta-pill">
                    <MessageOutlined className="meta-icon" />
                    <span className="meta-label">Communication</span>
                    <span className="meta-value">{selectedJob.communication}</span>
                  </span>
                </div>

                <Divider />

                <div className="skills-section">
                  <span className="skill-section-label primary-label">Primary Skills</span>
                  <Space wrap size={[6, 6]}>
                    {selectedJob.primarySkills?.map((skill) => (
                      <Tag key={skill} color="geekblue">{skill}</Tag>
                    ))}
                  </Space>
                </div>

                {selectedJob.goodToHaveSkills?.length > 0 && (
                  <div className="skills-section">
                    <span className="skill-section-label good-label">Good to Have</span>
                    <Space wrap size={[6, 6]}>
                      {selectedJob.goodToHaveSkills?.map((skill) => (
                        <Tag key={skill} color="cyan">{skill}</Tag>
                      ))}
                    </Space>
                  </div>
                )}

                <div className="info-grid">
                  <div className="info-cell">
                    <CalendarOutlined className="info-icon" />
                    <div className="info-cell-content">
                      <span className="info-cell-label">Start Date</span>
                      <span className="info-cell-value">{selectedJob.startDate}</span>
                    </div>
                  </div>
                  <div className="info-cell">
                    <ClockCircleOutlined className="info-icon" />
                    <div className="info-cell-content">
                      <span className="info-cell-label">Work Time</span>
                      <span className="info-cell-value">{selectedJob.workTime}</span>
                    </div>
                  </div>
                  <div className="info-cell">
                    <LaptopOutlined className="info-icon" />
                    <div className="info-cell-content">
                      <span className="info-cell-label">System</span>
                      <Tag
                        color={selectedJob.systemProvided ? "success" : "error"}
                        style={{ marginTop: 2 }}
                      >
                        {selectedJob.systemProvided ? "Provided" : "Not Provided"}
                      </Tag>
                    </div>
                  </div>
                  <div className="info-cell">
                    <EnvironmentOutlined className="info-icon" />
                    <div className="info-cell-content">
                      <span className="info-cell-label">Time Zone</span>
                      <span className="info-cell-value">{selectedJob.timeZone}</span>
                    </div>
                  </div>
                </div>
                
                <Divider />

                {matchCounts[selectedJob.id] !== undefined && (
                  <Row style={{ marginBottom: 16 }}>
                    <Col span={24}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        <div style={{
                          background: matchCounts[selectedJob.id].count > 0 ? "#e6fff9" : "#f5f5f5",
                          border: `1px solid ${matchCounts[selectedJob.id].count > 0 ? "#00d9a9" : "#d9d9d9"}`,
                          borderRadius: 6,
                          padding: "6px 14px",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 8,
                          fontSize: 13,
                          fontWeight: 600,
                          color: matchCounts[selectedJob.id].count > 0 ? "#00956e" : "#8c8c8c",
                        }}>
                          <UserOutlined />
                          Matching profiles from your bench: {matchCounts[selectedJob.id].count}
                        </div>
                        {matchCounts[selectedJob.id].count > 0 && (() => {
                          const pct = matchCounts[selectedJob.id].bestPct;
                          const isHigh = pct >= 70;
                          const isMid = pct >= 40;
                          return (
                            <div style={{
                              background: isHigh ? "#f6ffed" : isMid ? "#fffbe6" : "#f5f5f5",
                              border: `1px solid ${isHigh ? "#b7eb8f" : isMid ? "#ffe58f" : "#d9d9d9"}`,
                              borderRadius: 6,
                              padding: "6px 14px",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 6,
                              fontSize: 13,
                              fontWeight: 600,
                              color: isHigh ? "#389e0d" : isMid ? "#d48806" : "#8c8c8c",
                            }}>
                              ⚡ Best match: {pct}%
                            </div>
                          );
                        })()}
                      </div>
                    </Col>
                  </Row>
                )}

                <Row className="description-section">
                  <Col span={24}>
                    <Text strong className="description-title">Job Description</Text>
                    <div
                      className="description-text"
                      dangerouslySetInnerHTML={{ __html: selectedJob.jobDescription }}
                    />
                  </Col>
                </Row>
              </>
            ) : (
              <div className="empty-state">
                <Empty description="Select a job card to view details." />
              </div>
            )}
          </Card>
        </Col>
      </Row>
    </OngoingJobsWrapper>
  );
};

export default OngoingJobs;
